/**
 * Componente de guardia de permisos
 * Fecha: 16 Oct 2025
 * 
 * Muestra contenido solo si el usuario tiene los permisos necesarios
 */

import React from 'react';
import { usePermissions } from '@/shared/hooks/usePermissions';

interface PermissionGuardProps {
  /** Permiso requerido (string) o lista de permisos (array) */
  permission: string | string[];
  /** Si es true, require TODOS los permisos. Si es false, require AL MENOS UNO */
  requireAll?: boolean;
  /** Componente a mostrar si no tiene permisos */
  fallback?: React.ReactNode;
  /** Contenido a proteger */
  children: React.ReactNode;
}

/**
 * Componente que envuelve contenido y lo muestra solo si el usuario tiene permisos
 * 
 * @example
 * // Un solo permiso
 * <PermissionGuard permission="employees_create">
 *   <Button>Crear Empleado</Button>
 * </PermissionGuard>
 * 
 * @example
 * // Múltiples permisos (al menos uno)
 * <PermissionGuard permission={["employees_create", "employees_update"]}>
 *   <Button>Editar</Button>
 * </PermissionGuard>
 * 
 * @example
 * // Requiere TODOS los permisos
 * <PermissionGuard permission={["employees_create", "contracts_generate"]} requireAll>
 *   <Button>Contratar y Generar Contrato</Button>
 * </PermissionGuard>
 * 
 * @example
 * // Con fallback personalizado
 * <PermissionGuard
 *   permission="employees_delete"
 *   fallback={<span className="text-gray-400">Sin permisos</span>}
 * >
 *   <Button>Eliminar</Button>
 * </PermissionGuard>
 */
export const PermissionGuard: React.FC<PermissionGuardProps> = ({
  permission,
  requireAll = false,
  fallback = null,
  children,
}) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissions();

  let isAllowed = false;

  if (Array.isArray(permission)) {
    // Si es un array de permisos
    isAllowed = requireAll
      ? hasAllPermissions(permission)
      : hasAnyPermission(permission);
  } else {
    // Si es un solo permiso
    isAllowed = hasPermission(permission);
  }

  return isAllowed ? <>{children}</> : <>{fallback}</>;
};
